import { ImpactType } from "@ecologi/shared";
import ShadowSvg from "@ecologi/assets/icons/impact-shop/shadow.svg";
import Sapling from "@ecologi/assets/icons/impact-shop/sapling.svg";
import WildFlower from "@ecologi/assets/icons/impact-shop/wildflower.svg";
import WildFlowerOnWhite from "@ecologi/assets/icons/impact-shop/wildflower-on-white.svg";
import CarbonCloud from "@ecologi/assets/icons/impact-shop/carbon-cloud.svg";
import Turbine from "@ecologi/assets/icons/impact-shop/turbine.svg";
import TurbineOnMist from "@ecologi/assets/icons/impact-shop/turbine-on-mist.svg";
import styled from "@emotion/styled";
import useHover from "react-use/lib/useHover";
import { Typography } from "../Typography";

const ICONS = {
  [ImpactType.TREES]: {
    bgWhite: Sapling,
    bgMist: Sapling,
    bgForest: Sapling,
  },
  [ImpactType.CARBON_OFFSETS]: {
    bgWhite: Turbine,
    bgMist: TurbineOnMist,
    bgForest: Turbine,
  },
  [ImpactType.CARBON_REMOVAL]: {
    bgWhite: CarbonCloud,
    bgMist: CarbonCloud,
    bgForest: CarbonCloud,
  },
  [ImpactType.HABITAT_RESTORATION]: {
    bgWhite: WildFlowerOnWhite,
    bgMist: WildFlower,
    bgForest: WildFlower,
  },
};

const Icon = styled(
  ({
    icon,
    className,
  }: {
    icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
    className?: string;
  }) => {
    const InnerIcon = icon;
    return <InnerIcon className={className} />;
  }
)`
  height: 52px;
  position: absolute;
  left: 8px;
  top: -20px;
`;

const StyledButton = styled.button<{ selected?: boolean }>`
  background-color: ${({ selected, theme }) =>
    selected ? theme.colors.forestLight : theme.colors.snowdrop};
  border-radius: 30px;
  border: none;
  padding: 14px 24px;
  margin: 8px 0;
  min-width: 96px;
  cursor: pointer;

  :focus-visible {
    outline: ${(props) => `2px solid ${props.theme.colors.forest}`};
    outline-offset: 2px;
  }

  :hover {
    background-color: ${({ selected, theme }) =>
      selected ? theme.colors.forest : theme.colors.mistDark};
  }
`;

const StyledButtonWithImage = styled(StyledButton)`
  padding: 14px 24px 14px 60px;
  position: relative;
`;

const Shadow = styled(ShadowSvg)<{ selected: boolean }>`
  position: absolute;
  top: 27px;
  left: 17px;
  opacity: ${({ selected }) => (selected ? 0.12 : 0.06)};
`;

const StyledTypography = styled(Typography)<{ selected: boolean }>`
  color: ${({ selected, theme }) =>
    selected ? theme.colors.snowdrop : theme.colors.charcoal};
`;

export const FilterButton = ({
  impactType,
  selected,
  onClick,
  children,
}: {
  impactType: ImpactType | null;
  selected: boolean;
  onClick: () => void;
  children: string;
}) => {
  if (impactType !== null) {
    const [hoverable] = useHover((hovered) => {
      const icon = selected
        ? ICONS[impactType].bgForest
        : hovered
        ? ICONS[impactType].bgMist
        : ICONS[impactType].bgWhite;
      return (
        <StyledButtonWithImage selected={selected} onClick={onClick}>
          <Icon icon={icon} />
          <Shadow selected={selected} />
          <StyledTypography use="body3" selected={selected}>
            {children}
          </StyledTypography>
        </StyledButtonWithImage>
      );
    });
    return hoverable;
  } else {
    return (
      <StyledButton selected={selected} onClick={onClick}>
        <StyledTypography use="body3" selected={selected}>
          {children}
        </StyledTypography>
      </StyledButton>
    );
  }
};
